import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Menu,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Publish as PublishIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Cloud as CloudIcon,
  MoreVert as MoreVertIcon,
  GetApp as GetAppIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';
import { rangeosApiService } from '../services/rangeos-api';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../services/rangeos-environment-api';
import { scenarioValidationService, ScenarioValidationReport, ReferenceValidationDetail, SuggestedMatch } from '../services/scenario-validation-api';
import { SchemaMigrationManager } from './SchemaMigrationManager';

interface UuidMapping {
  oldUuid: string;
  newUuid: string;
  type: string;
  resourceName?: string;
}

export const DraftManager: React.FC = () => {
  const [environments, setEnvironments] = useState<RangeOSEnvironment[]>([]);
  const [s3Environments, setS3Environments] = useState<any[]>([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');
  const [drafts, setDrafts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Validation state
  const [selectedDrafts, setSelectedDrafts] = useState<string[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [validationReports, setValidationReports] = useState<ScenarioValidationReport[]>([]);
  const [uuidMappings, setUuidMappings] = useState<Map<string, Map<string, UuidMapping>>>(new Map());
  const [validating, setValidating] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [transformRoleSchemas, setTransformRoleSchemas] = useState(false);
  const [transformingSchemas, setTransformingSchemas] = useState(false);

  // Schema migration dialog
  const [schemaMigrationDialogOpen, setSchemaMigrationDialogOpen] = useState(false);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Version history dialog
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [selectedDraftForVersions, setSelectedDraftForVersions] = useState<any>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Match selection dialog
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [currentMatch, setCurrentMatch] = useState<{
    draftId: string;
    detail: ReferenceValidationDetail;
  } | null>(null);
  const [selectedMatchUuid, setSelectedMatchUuid] = useState<string>('');
  const [manualUuidInput, setManualUuidInput] = useState<string>('');
  const [availableResources, setAvailableResources] = useState<any[]>([]);
  const [loadingResources, setLoadingResources] = useState(false);

  // Publish confirmation dialog
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const [publishTarget, setPublishTarget] = useState<{ draftId: string; draftName: string } | null>(null);

  // Validation results dialog
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [selectedValidationTab, setSelectedValidationTab] = useState(0);
  const [selectedResourceTypeTab, setSelectedResourceTypeTab] = useState(0);

  // Import/Export state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importTabValue, setImportTabValue] = useState(0); // 0 = Local File, 1 = S3, 2 = RangeOS
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importDraftName, setImportDraftName] = useState('');
  const [importDraftDescription, setImportDraftDescription] = useState('');
  const [importS3Environment, setImportS3Environment] = useState('');
  const [importS3Files, setImportS3Files] = useState<any[]>([]);
  const [selectedImportS3File, setSelectedImportS3File] = useState('');
  const [loadingImportS3Files, setLoadingImportS3Files] = useState(false);
  const [importSourceRangeOS, setImportSourceRangeOS] = useState('');
  const [importSourceDrafts, setImportSourceDrafts] = useState<any[]>([]);
  const [selectedImportDraft, setSelectedImportDraft] = useState('');
  const [loadingImportDrafts, setLoadingImportDrafts] = useState(false);
  const [importing, setImporting] = useState(false);

  // Export menu
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exportDraft, setExportDraft] = useState<{ id: string; name: string } | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportToS3, setExportToS3] = useState(false);
  const [exportS3Environment, setExportS3Environment] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadEnvironments();
  }, []);

  useEffect(() => {
    if (selectedEnvironment) {
      loadDrafts();
    } else {
      setDrafts([]);
      setSelectedDrafts([]);
      setValidationReports([]);
    }
  }, [selectedEnvironment]);

  const loadEnvironments = async () => {
    setLoading(true);
    setError(null);

    try {
      const [rangeosEnvs, s3Envs] = await Promise.all([
        rangeosEnvironmentService.getActiveEnvironments(),
        (async () => {
          try {
            const { basePrefixApi } = await import('../services/api');
            return await basePrefixApi.getEnvironmentsWithBuckets();
          } catch (err) {
            console.error('[Draft Manager] Failed to load S3 environments:', err);
            return [];
          }
        })()
      ]);
      setEnvironments(rangeosEnvs);
      setS3Environments(s3Envs);
    } catch (err: any) {
      console.error('[Draft Manager] Failed to load environments:', err);
      setError(`Failed to load environments: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadDrafts = async () => {
    if (!selectedEnvironment) return;

    setLoading(true);
    setError(null);

    try {
      const draftList = await rangeosApiService.listDesignDrafts(selectedEnvironment);
      setDrafts(draftList);
    } catch (err: any) {
      console.error('[Draft Manager] Failed to load drafts:', err);
      setError(`Failed to load drafts: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (draftId: string, draftName: string) => {
    setDraftToDelete({ id: draftId, name: draftName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDraft = async () => {
    if (!draftToDelete) return;

    setDeleting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await rangeosApiService.deleteDraft(selectedEnvironment, draftToDelete.id);
      setSuccessMessage(`Successfully deleted draft "${draftToDelete.name}"`);
      setDeleteDialogOpen(false);
      setDraftToDelete(null);

      // Remove from validation reports and selected drafts
      setValidationReports(prev => prev.filter(r => r.draftId !== draftToDelete.id));
      setSelectedDrafts(prev => prev.filter(id => id !== draftToDelete.id));

      // Reload drafts
      await loadDrafts();
    } catch (err: any) {
      console.error('[Draft Manager] Failed to delete draft:', err);
      setError(`Failed to delete draft: ${err.message || 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const openVersionsDialog = async (draft: any) => {
    setSelectedDraftForVersions(draft);
    setVersionDialogOpen(true);
    setLoadingVersions(true);

    try {
      const stateData = await rangeosApiService.getDesignState(selectedEnvironment, draft.id);
      const versionsList = stateData?.state?.data || [];
      setVersions(versionsList);
    } catch (err: any) {
      console.error('[Draft Manager] Failed to load versions:', err);
      setError(`Failed to load versions: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleSelectDraft = (draftId: string, checked: boolean, event?: React.MouseEvent) => {
    const currentIndex = filteredDrafts.findIndex(d => d.id === draftId);

    // Handle shift-click for range selection
    if (event?.shiftKey && lastSelectedIndex !== null && currentIndex !== -1) {
      const start = Math.min(lastSelectedIndex, currentIndex);
      const end = Math.max(lastSelectedIndex, currentIndex);
      const rangeIds = filteredDrafts.slice(start, end + 1).map(d => d.id);

      if (checked) {
        // Add all items in range to selection
        const newSelection = new Set([...selectedDrafts, ...rangeIds]);
        setSelectedDrafts(Array.from(newSelection));
      } else {
        // Remove all items in range from selection
        setSelectedDrafts(selectedDrafts.filter(id => !rangeIds.includes(id)));
      }
    } else {
      // Normal single selection
      if (checked) {
        setSelectedDrafts([...selectedDrafts, draftId]);
      } else {
        setSelectedDrafts(selectedDrafts.filter(id => id !== draftId));
      }
    }

    // Always update last selected index for shift-click tracking
    setLastSelectedIndex(currentIndex);
  };

  const handleSelectAllDrafts = (checked: boolean) => {
    if (checked) {
      setSelectedDrafts(filteredDrafts.map(d => d.id));
    } else {
      setSelectedDrafts([]);
    }
  };

  const handleValidateDrafts = async () => {
    if (selectedDrafts.length === 0) {
      setError('Please select at least one draft to validate');
      return;
    }

    setValidating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const reports = await scenarioValidationService.validateScenarios(
        selectedEnvironment,
        selectedDrafts
      );

      // Merge new reports with existing ones (update if exists, add if new)
      setValidationReports(prev => {
        const merged = [...prev];
        reports.forEach(newReport => {
          const existingIndex = merged.findIndex(r => r.draftId === newReport.draftId);
          if (existingIndex >= 0) {
            // Replace existing report
            merged[existingIndex] = newReport;
          } else {
            // Add new report
            merged.push(newReport);
          }
        });
        return merged;
      });

      const invalidCount = reports.filter(r => !r.overallValid).length;
      if (invalidCount === 0) {
        setSuccessMessage(`All ${reports.length} draft(s) are valid!`);
      } else {
        setError(`${invalidCount} of ${reports.length} draft(s) have validation issues`);

        // Only open validation dialog if there are invalid drafts
        setSelectedValidationTab(0);
        setSelectedResourceTypeTab(0);
        setValidationDialogOpen(true);
      }
    } catch (err: any) {
      console.error('[Draft Manager] Validation failed:', err);
      setError(`Validation failed: ${err.message || 'Unknown error'}`);
    } finally {
      setValidating(false);
    }
  };

  const openMatchDialog = async (draftId: string, detail: ReferenceValidationDetail) => {
    setCurrentMatch({ draftId, detail });
    setSelectedMatchUuid(detail.suggestedMatches?.[0]?.uuid || '');
    setManualUuidInput('');
    setMatchDialogOpen(true);

    // Load available resources of this type if no suggested matches
    if (!detail.suggestedMatches || detail.suggestedMatches.length === 0) {
      await loadAvailableResources(detail.type);
    }
  };

  const loadAvailableResources = async (resourceType: string) => {
    if (!selectedEnvironment) return;

    setLoadingResources(true);
    setAvailableResources([]);

    try {
      let resources: any[] = [];

      // Map the type to the appropriate API call
      switch (resourceType.toUpperCase()) {
        case 'VMIMAGE':
          resources = await rangeosApiService.listVMImages(selectedEnvironment);
          break;
        case 'ANSIBLEROLE':
          resources = await rangeosApiService.listAnsibleRoles(selectedEnvironment);
          break;
        case 'CLUSTERRANGENETWORK':
        case 'RANGEL3NETWORK':
          resources = await rangeosApiService.listRangeL3Networks(selectedEnvironment);
          break;
        case 'RANGENETWORK':
          resources = await rangeosApiService.listRangeNetworks(selectedEnvironment);
          break;
        case 'NETWORK':
          resources = await rangeosApiService.listNetworks(selectedEnvironment);
          break;
        case 'CHART':
          resources = await rangeosApiService.listCharts(selectedEnvironment);
          break;
        case 'GHOSTTRAFFIC':
        case 'GHOSTTRAFFICPROFILE':
          resources = await rangeosApiService.listGhostTraffic(selectedEnvironment);
          break;
        default:
          console.warn(`[Draft Manager] Unknown resource type for browsing: ${resourceType}`);
      }

      setAvailableResources(resources);
    } catch (err: any) {
      console.error(`[Draft Manager] Failed to load ${resourceType} resources:`, err);
    } finally {
      setLoadingResources(false);
    }
  };

  const confirmMatch = () => {
    const uuidToUse = selectedMatchUuid || manualUuidInput.trim();
    if (!currentMatch || !uuidToUse) return;

    const { draftId, detail } = currentMatch;
    const draftMappings = uuidMappings.get(draftId) || new Map<string, UuidMapping>();
    const selectedMatch = detail.suggestedMatches?.find(m => m.uuid === uuidToUse);
    const selectedResource = availableResources.find(r => r.uuid === uuidToUse);

    draftMappings.set(detail.uuid, {
      oldUuid: detail.uuid,
      newUuid: uuidToUse,
      type: detail.type,
      resourceName: selectedMatch?.name || selectedResource?.name || 'Manual mapping',
    });

    const newMappings = new Map(uuidMappings);
    newMappings.set(draftId, draftMappings);
    setUuidMappings(newMappings);

    setMatchDialogOpen(false);
    setCurrentMatch(null);
    setSelectedMatchUuid('');
    setManualUuidInput('');
    setAvailableResources([]);
  };

  const autoMapHighConfidenceMatches = (draftId: string, report: ScenarioValidationReport) => {
    const draftMappings = uuidMappings.get(draftId) || new Map<string, UuidMapping>();
    let matchCount = 0;

    Object.values(report.details.byType).forEach((typeDetails) => {
      typeDetails.references.forEach((detail: ReferenceValidationDetail) => {
        if (detail.exists || draftMappings.has(detail.uuid)) return;

        const highConfidenceMatch = detail.suggestedMatches?.find(m => m.confidence === 'high');
        if (highConfidenceMatch) {
          draftMappings.set(detail.uuid, {
            oldUuid: detail.uuid,
            newUuid: highConfidenceMatch.uuid,
            type: detail.type,
            resourceName: highConfidenceMatch.name,
          });
          matchCount++;
        }
      });
    });

    if (matchCount > 0) {
      const newMappings = new Map(uuidMappings);
      newMappings.set(draftId, draftMappings);
      setUuidMappings(newMappings);
      setSuccessMessage(`Auto-mapped ${matchCount} high confidence match(es)`);
    } else {
      setError('No high confidence matches found to auto-map');
    }
  };

  const handleApplyMappings = async (draftId: string) => {
    const draftMappings = uuidMappings.get(draftId);
    if (!draftMappings || draftMappings.size === 0) {
      setError('No mappings to apply');
      return;
    }

    setPublishing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const mappingsArray = Array.from(draftMappings.values());
      const updateResult = await scenarioValidationService.updateDraftReferences(
        selectedEnvironment,
        draftId,
        mappingsArray,
        { transformRoleSchemas }
      );

      if (!updateResult.success) {
        throw new Error(updateResult.message);
      }

      const schemaMsg = updateResult.schemaTransformations && updateResult.schemaTransformations > 0
        ? ` and transformed ${updateResult.schemaTransformations} role schema(s)`
        : '';
      setSuccessMessage(`Successfully updated draft with ${mappingsArray.length} UUID mapping(s)${schemaMsg}`);

      // Clear mappings for this draft first
      const newMappings = new Map(uuidMappings);
      newMappings.delete(draftId);
      setUuidMappings(newMappings);

      // Re-validate this scenario
      setRevalidating(true);
      const reports = await scenarioValidationService.validateScenarios(
        selectedEnvironment,
        [draftId]
      );

      // Update validation reports
      setValidationReports(prev => {
        const merged = [...prev];
        const existingIndex = merged.findIndex(r => r.draftId === draftId);
        if (existingIndex >= 0) {
          merged[existingIndex] = reports[0];
        } else {
          merged.push(reports[0]);
        }
        return merged;
      });

      // Reset to first resource type tab to see updated results
      setSelectedResourceTypeTab(0);
    } catch (err: any) {
      console.error('[Draft Manager] Failed to apply mappings:', err);
      setError(`Failed to apply mappings: ${err.message || 'Unknown error'}`);
    } finally {
      setPublishing(false);
      setRevalidating(false);
    }
  };

  const openPublishDialog = (draftId: string, draftName: string) => {
    setPublishTarget({ draftId, draftName });
    setConfirmPublishOpen(true);
  };

  const handlePublishDraft = async () => {
    if (!publishTarget) return;

    setConfirmPublishOpen(false);
    setPublishing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await scenarioValidationService.publishDraft(
        selectedEnvironment,
        publishTarget.draftId
      );

      if (!result.success) {
        throw new Error(result.message);
      }

      const scenarioId = result.publishedId ? ` (Scenario ID: ${result.publishedId})` : '';
      setSuccessMessage(`Successfully published draft "${publishTarget.draftName}" as scenario${scenarioId}`);

      // Refresh the draft list to show the updated published status
      await loadDrafts();
    } catch (err: any) {
      console.error('[Draft Manager] Failed to publish:', err);
      setError(`Failed to publish: ${err.message || 'Unknown error'}`);
    } finally {
      setPublishing(false);
      setPublishTarget(null);
    }
  };

  const handleTransformSchemas = async () => {
    if (validationReports.length === 0) return;

    setTransformingSchemas(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Transform schemas for all drafts in the validation reports
      let totalTransformations = 0;
      const allAppliedRules: string[] = [];
      const errors: string[] = [];

      for (const report of validationReports) {
        console.log(`[Draft Manager] Transforming schemas in draft ${report.draftId}`);
        const result = await scenarioValidationService.transformDraftSchemas(
          selectedEnvironment,
          report.draftId
        );

        if (result.success) {
          totalTransformations += result.schemaTransformations;
          allAppliedRules.push(...result.appliedRules);
        } else {
          errors.push(`${report.draftId}: ${result.message}`);
        }

        if (result.errors.length > 0) {
          errors.push(...result.errors.map(e => `${report.draftId}: ${e}`));
        }
      }

      if (totalTransformations > 0) {
        setSuccessMessage(`Successfully transformed ${totalTransformations} role schema(s). Applied rules: ${[...new Set(allAppliedRules)].join(', ')}`);
        // Re-validate to show updated status
        await handleValidateDrafts();
      } else {
        setError('No schema transformations were applied. The role schemas may already be in the correct format, or no matching migration rules were found.');
      }

      if (errors.length > 0) {
        console.warn('[Draft Manager] Transform schema errors:', errors);
      }
    } catch (err: any) {
      console.error('[Draft Manager] Failed to transform schemas:', err);
      setError(`Failed to transform schemas: ${err.message || 'Unknown error'}`);
    } finally {
      setTransformingSchemas(false);
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  // Export handlers
  const openExportMenu = (event: React.MouseEvent<HTMLElement>, draft: any) => {
    setExportMenuAnchor(event.currentTarget);
    setExportDraft({ id: draft.id, name: draft.name });
  };

  const closeExportMenu = () => {
    setExportMenuAnchor(null);
    setExportDraft(null);
  };

  const handleExportToLocal = async () => {
    if (!exportDraft) return;
    closeExportMenu();
    setExporting(true);

    try {
      const scenarioDef = await rangeosApiService.getDraftScenarioDefinition(selectedEnvironment, exportDraft.id);
      const filename = `${exportDraft.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.json`;
      const blob = new Blob([JSON.stringify(scenarioDef, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccessMessage(`Successfully exported draft "${exportDraft.name}" to ${filename}`);
    } catch (err: any) {
      console.error('[Draft Manager] Export to local failed:', err);
      setError(`Failed to export: ${err.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportToS3 = () => {
    setExportMenuAnchor(null); // Close menu but keep exportDraft
    setExportToS3(true);
    setExportDialogOpen(true);
  };

  const confirmExportToS3 = async () => {
    if (!exportDraft || !exportS3Environment) return;

    setExporting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await rangeosApiService.exportDraftToS3(
        selectedEnvironment,
        exportDraft.id,
        {
          targetEnvironment: exportS3Environment,
        }
      );

      setSuccessMessage(`Successfully exported draft "${exportDraft.name}" to S3: ${result.s3Key}`);
      setExportDialogOpen(false);
      setExportS3Environment('');
    } catch (err: any) {
      console.error('[Draft Manager] Export to S3 failed:', err);
      setError(`Failed to export to S3: ${err.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  // Import handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportDraftName(file.name.replace('.json', ''));
    }
  };

  const handleImportFromLocal = async () => {
    if (!importFile || !importDraftName) {
      setError('Please select a file and provide a draft name');
      return;
    }

    setImporting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const fileContent = await importFile.text();
      const scenarioDef = JSON.parse(fileContent);

      const result = await rangeosApiService.importDraftFromJSON(
        selectedEnvironment,
        {
          name: importDraftName,
          description: importDraftDescription,
          scenarioDefinition: scenarioDef,
        }
      );

      setSuccessMessage(`Successfully imported draft "${result.draftId}"`);
      setImportDialogOpen(false);
      setImportFile(null);
      setImportDraftName('');
      setImportDraftDescription('');

      // Reload drafts
      await loadDrafts();
    } catch (err: any) {
      console.error('[Draft Manager] Import from local failed:', err);
      setError(`Failed to import: ${err.message || 'Unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  const loadS3Files = async (environment: string) => {
    setLoadingImportS3Files(true);
    try {
      const { s3BucketsApi } = await import('../services/s3-buckets-api');
      const files = await s3BucketsApi.listFiles(environment, 'rangeos/drafts');
      setImportS3Files(files);
    } catch (err: any) {
      console.error('[Draft Manager] Failed to load S3 files:', err);
      setError(`Failed to load S3 files: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingImportS3Files(false);
    }
  };

  const handleImportS3EnvironmentChange = (environment: string) => {
    setImportS3Environment(environment);
    if (environment) {
      loadS3Files(environment);
    }
  };

  const handleImportFromS3 = async () => {
    if (!selectedImportS3File || !importDraftName) {
      setError('Please select an S3 file and provide a draft name');
      return;
    }

    setImporting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await rangeosApiService.importDraftFromS3(
        selectedEnvironment,
        {
          sourceEnvironment: importS3Environment,
          s3Key: selectedImportS3File,
          draftName: importDraftName,
        }
      );

      setSuccessMessage(`Successfully imported draft "${result.draftName}"`);
      setImportDialogOpen(false);
      setSelectedImportS3File('');
      setImportDraftName('');
      setImportDraftDescription('');

      // Reload drafts
      await loadDrafts();
    } catch (err: any) {
      console.error('[Draft Manager] Import from S3 failed:', err);
      setError(`Failed to import from S3: ${err.message || 'Unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  const handleImportSourceRangeOSChange = async (sourceEndpointId: string) => {
    setImportSourceRangeOS(sourceEndpointId);
    setSelectedImportDraft('');
    setImportSourceDrafts([]);

    if (!sourceEndpointId) return;

    setLoadingImportDrafts(true);
    try {
      const draftList = await rangeosApiService.listDesignDrafts(sourceEndpointId);
      setImportSourceDrafts(draftList);
    } catch (err: any) {
      console.error('[Draft Manager] Failed to load source drafts:', err);
      setError(`Failed to load drafts from source: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingImportDrafts(false);
    }
  };

  const handleImportFromRangeOS = async () => {
    if (!selectedImportDraft) {
      setError('Please select a draft to import');
      return;
    }

    setImporting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Find the selected draft to get its name
      const sourceDraft = importSourceDrafts.find(d => d.id === selectedImportDraft);
      const draftName = importDraftName || sourceDraft?.name || 'Imported Draft';

      const result = await rangeosApiService.transferDraft({
        sourceEndpointId: importSourceRangeOS,
        sourceDraftId: selectedImportDraft,
        targetEndpointId: selectedEnvironment,
        draftName,
      });

      setSuccessMessage(`Successfully imported draft "${result.draftName}" from RangeOS`);
      setImportDialogOpen(false);
      setImportSourceRangeOS('');
      setSelectedImportDraft('');
      setImportSourceDrafts([]);
      setImportDraftName('');
      setImportDraftDescription('');

      // Reload drafts
      await loadDrafts();
    } catch (err: any) {
      console.error('[Draft Manager] Import from RangeOS failed:', err);
      setError(`Failed to import from RangeOS: ${err.message || 'Unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  // Filter drafts based on search query
  const filteredDrafts = drafts.filter(draft => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      draft.name?.toLowerCase().includes(query) ||
      draft.description?.toLowerCase().includes(query) ||
      draft.id?.toLowerCase().includes(query)
    );
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        RangeOS Draft Manager
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Manage draft scenarios: validate, view details, delete drafts, inspect versions, and publish.
      </Typography>

      {/* Environment Selection */}
      <Card sx={{ mb: 3, mt: 3 }}>
        <CardContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>RangeOS Environment</InputLabel>
            <Select
              value={selectedEnvironment}
              onChange={(e) => setSelectedEnvironment(e.target.value)}
              label="RangeOS Environment"
              disabled={loading}
            >
              {environments.map((env) => (
                <MenuItem key={env.id} value={env.id}>
                  {env.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loading && <LinearProgress />}
        </CardContent>
      </Card>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Drafts List */}
      {selectedEnvironment && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Drafts ({filteredDrafts.length} {filteredDrafts.length !== drafts.length ? `of ${drafts.length}` : ''})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleValidateDrafts}
                  disabled={validating || selectedDrafts.length === 0}
                  startIcon={<CheckCircleIcon />}
                >
                  {validating ? 'Validating...' : `Validate Selected (${selectedDrafts.length})`}
                </Button>
                {validationReports.length > 0 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setValidationDialogOpen(true)}
                    startIcon={<CheckCircleIcon />}
                  >
                    View Validation Results ({validationReports.length})
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<FileUploadIcon />}
                  onClick={() => setImportDialogOpen(true)}
                  disabled={loading}
                >
                  Import Draft
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadDrafts}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            <TextField
              fullWidth
              placeholder="Search drafts by name, description, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              size="small"
            />

            {filteredDrafts.length === 0 ? (
              <Alert severity="info">
                {drafts.length === 0
                  ? 'No drafts found in this environment.'
                  : `No drafts match "${searchQuery}"`
                }
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDrafts.length === filteredDrafts.length && filteredDrafts.length > 0}
                          indeterminate={selectedDrafts.length > 0 && selectedDrafts.length < filteredDrafts.length}
                          onChange={(e) => handleSelectAllDrafts(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Draft ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDrafts.map((draft) => {
                      const report = validationReports.find(r => r.draftId === draft.id);
                      const isValid = report?.overallValid === true;
                      const isPublishDisabled = !isValid || publishing;

                      return (
                        <TableRow key={`${draft.id}-${report?.overallValid || 'no-report'}`}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedDrafts.includes(draft.id)}
                              onClick={(e) => handleSelectDraft(draft.id, !selectedDrafts.includes(draft.id), e)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {draft.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {draft.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {draft.id.substring(0, 12)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {(draft as any).publishedScenarioId && (
                                <Tooltip title={`Published as scenario: ${(draft as any).publishedScenarioId}`}>
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Published"
                                    color="info"
                                    size="small"
                                  />
                                </Tooltip>
                              )}
                              {report && (
                                report.overallValid ? (
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Valid"
                                    color="success"
                                    size="small"
                                  />
                                ) : (
                                  <Chip
                                    icon={<ErrorIcon />}
                                    label={`${report.summary.missingReferences} missing`}
                                    color="error"
                                    size="small"
                                  />
                                )
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Tooltip title={
                                !isValid ? "Validate draft first to enable publishing" :
                                (draft as any).publishedScenarioId ? "Re-publish Scenario (creates new version)" :
                                "Publish as Scenario"
                              }>
                                <span>
                                  <IconButton
                                    size="small"
                                    color={isValid ? "primary" : "default"}
                                    onClick={() => openPublishDialog(draft.id, draft.name)}
                                    disabled={isPublishDisabled}
                                  >
                                    <PublishIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="View Version History">
                                <IconButton
                                  size="small"
                                  onClick={() => openVersionsDialog(draft)}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Export">
                                <IconButton
                                  size="small"
                                  onClick={(e) => openExportMenu(e, draft)}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Draft">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => openDeleteDialog(draft.id, draft.name)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Validation Results Dialog */}
      <Dialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5">Validation Results</Typography>
            <IconButton onClick={() => setValidationDialogOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, position: 'relative' }}>
          {/* Loading overlay for re-validation */}
          {revalidating && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 10,
                gap: 2,
              }}
            >
              <CircularProgress size={60} />
              <Typography variant="body1" color="text.secondary">
                Re-validating draft...
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Left sidebar with tabs */}
            <Box sx={{ borderRight: 1, borderColor: 'divider', minWidth: 280, maxWidth: 280 }}>
              <Tabs
                orientation="vertical"
                value={selectedValidationTab}
                onChange={(e, newValue) => {
                  setSelectedValidationTab(newValue);
                  setSelectedResourceTypeTab(0); // Reset resource type tab when switching drafts
                }}
                sx={{
                  '& .MuiTab-root': {
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    minHeight: 72,
                  }
                }}
              >
                {validationReports.map((report, index) => {
                  const draftMappings = uuidMappings.get(report.draftId);
                  const hasMappings = draftMappings && draftMappings.size > 0;

                  return (
                    <Tab
                      key={report.draftId}
                      value={index}
                      label={
                        <Box sx={{ width: '100%', py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {report.overallValid ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <ErrorIcon color="error" fontSize="small" />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {report.scenarioName}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {!report.overallValid && (
                              <Chip
                                label={`${report.summary.missingReferences} missing`}
                                color="error"
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {hasMappings && (
                              <Chip
                                label={`${draftMappings.size} mapping(s)`}
                                color="warning"
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>

            {/* Right content area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {validationReports.map((report, index) => {
                if (index !== selectedValidationTab) return null;

                const draftMappings = uuidMappings.get(report.draftId);
                const hasMappings = draftMappings && draftMappings.size > 0;
                const resourceTypes = Object.keys(report.details.byType);

                return (
                  <Box
                    key={`${report.draftId}-${report.timestamp}-${hasMappings}`}
                    sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                  >
                    {/* Summary */}
                    <Alert severity={report.overallValid ? 'success' : 'warning'} sx={{ m: 3, mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Summary:</strong> {report.summary.validReferences} valid, {report.summary.missingReferences} missing
                        out of {report.summary.totalReferences} total references
                      </Typography>
                    </Alert>

                    {/* Resource Type Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                      <Tabs
                        value={selectedResourceTypeTab}
                        onChange={(e, newValue) => setSelectedResourceTypeTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                      >
                        {resourceTypes.map((type, typeIndex) => {
                          const typeDetails = report.details.byType[type];
                          return (
                            <Tab
                              key={type}
                              value={typeIndex}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2">{type}</Typography>
                                  <Tooltip
                                    title={
                                      typeDetails.uniqueCount && typeDetails.uniqueCount < typeDetails.totalCount
                                        ? `${typeDetails.validCount} valid out of ${typeDetails.uniqueCount} unique resources (used ${typeDetails.totalCount} times in scenario)`
                                        : `${typeDetails.validCount} valid out of ${typeDetails.totalCount} references`
                                    }
                                  >
                                    <Chip
                                      label={
                                        typeDetails.uniqueCount && typeDetails.uniqueCount < typeDetails.totalCount
                                          ? `${typeDetails.validCount}/${typeDetails.uniqueCount} (${typeDetails.totalCount}×)`
                                          : `${typeDetails.validCount}/${typeDetails.totalCount}`
                                      }
                                      size="small"
                                      color={typeDetails.validCount === typeDetails.uniqueCount || typeDetails.validCount === typeDetails.totalCount ? 'success' : 'error'}
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  </Tooltip>
                                </Box>
                              }
                            />
                          );
                        })}
                      </Tabs>
                    </Box>

                    {/* Resource Type Content */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                      {Object.entries(report.details.byType).map(([type, typeDetails], typeIndex) => {
                        if (typeIndex !== selectedResourceTypeTab) return null;

                        return (
                          <Box key={type}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Status</TableCell>
                                <TableCell>Resource Name</TableCell>
                                <TableCell>Used By</TableCell>
                                <TableCell>Best Match</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {typeDetails.references.map((detail: ReferenceValidationDetail) => {
                                const hasMapping = draftMappings?.has(detail.uuid);
                                const mapping = draftMappings?.get(detail.uuid);
                                const bestMatch = detail.suggestedMatches?.[0];

                                return (
                                  <TableRow key={detail.uuid}>
                                    <TableCell>
                                      {detail.exists ? (
                                        <CheckCircleIcon color="success" fontSize="small" />
                                      ) : hasMapping ? (
                                        <WarningIcon color="warning" fontSize="small" />
                                      ) : (
                                        <ErrorIcon color="error" fontSize="small" />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title={`UUID: ${detail.uuid}`}>
                                        <Typography variant="body2">
                                          {detail.exists ? (
                                            detail.resourceName
                                          ) : hasMapping ? (
                                            <Box>
                                              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                                {detail.resourceName || 'Missing'}
                                              </Typography>
                                              <Typography variant="body2" color="warning.main">
                                                → {mapping?.resourceName || mapping?.newUuid.substring(0, 8) + '...'}
                                              </Typography>
                                            </Box>
                                          ) : (
                                            <Typography variant="body2" color="error">
                                              {/* Show a more helpful message if resourceName is just the UUID */}
                                              {detail.resourceName && detail.resourceName !== detail.uuid
                                                ? detail.resourceName
                                                : `Unknown (${detail.uuid.substring(0, 8)}...)`}
                                            </Typography>
                                          )}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      {detail.usedBy ? (
                                        <Typography variant="body2" sx={{ fontSize: '0.85rem', whiteSpace: 'pre-line' }}>
                                          {detail.usedBy}
                                        </Typography>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                          —
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {!detail.exists && hasMapping && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'warning.main' }}>
                                              {mapping?.resourceName || 'Mapped'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                              Mapped to: {mapping?.newUuid.substring(0, 8)}...
                                            </Typography>
                                          </Box>
                                          <Chip
                                            label="mapped"
                                            color="warning"
                                            size="small"
                                            sx={{ ml: 1 }}
                                          />
                                        </Box>
                                      )}
                                      {!detail.exists && !hasMapping && bestMatch && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                              {bestMatch.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                              {bestMatch.matchReason}
                                            </Typography>
                                          </Box>
                                          <Chip
                                            label={bestMatch.confidence}
                                            color={getConfidenceColor(bestMatch.confidence) as any}
                                            size="small"
                                            sx={{ ml: 1 }}
                                          />
                                        </Box>
                                      )}
                                      {!detail.exists && !hasMapping && !bestMatch && (
                                        <Typography variant="caption" color="text.secondary">
                                          No matches found
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {!detail.exists && !hasMapping && (
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          {bestMatch && (
                                            <Tooltip title="Accept this match">
                                              <IconButton
                                                size="small"
                                                color="success"
                                                onClick={() => {
                                                  const draftMappings = uuidMappings.get(report.draftId) || new Map<string, UuidMapping>();
                                                  draftMappings.set(detail.uuid, {
                                                    oldUuid: detail.uuid,
                                                    newUuid: bestMatch.uuid,
                                                    type: detail.type,
                                                    resourceName: bestMatch.name,
                                                  });
                                                  const newMappings = new Map(uuidMappings);
                                                  newMappings.set(report.draftId, draftMappings);
                                                  setUuidMappings(newMappings);
                                                }}
                                              >
                                                <CheckCircleIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                          <Tooltip title={bestMatch ? "View all matches" : "Browse & Map Resource"}>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              onClick={() => openMatchDialog(report.draftId, detail)}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      )}
                                      {!detail.exists && hasMapping && (
                                        <Tooltip title="Remove mapping">
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                              const draftMappings = uuidMappings.get(report.draftId);
                                              if (draftMappings) {
                                                draftMappings.delete(detail.uuid);
                                                const newMappings = new Map(uuidMappings);
                                                setUuidMappings(newMappings);
                                              }
                                            }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* Action Buttons for this resource type */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                          {!report.overallValid && (
                            <Button
                              variant="outlined"
                              color="success"
                              onClick={() => autoMapHighConfidenceMatches(report.draftId, report)}
                              disabled={publishing}
                              startIcon={<CheckCircleIcon />}
                            >
                              Auto-Map High Confidence Matches
                            </Button>
                          )}
                          {hasMappings && (
                            <Button
                              variant="contained"
                              color="warning"
                              onClick={() => handleApplyMappings(report.draftId)}
                              disabled={publishing}
                              startIcon={<EditIcon />}
                            >
                              Apply {draftMappings.size} Mapping(s)
                            </Button>
                          )}
                        </Box>
                      </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={transformRoleSchemas}
                  onChange={(e) => setTransformRoleSchemas(e.target.checked)}
                />
              }
              label={
                <Tooltip title="When enabled, roleVariables will be transformed to match the target role's schema if it has changed (e.g., flat to array format)">
                  <Typography variant="body2">
                    Transform role schemas on apply
                  </Typography>
                </Tooltip>
              }
            />
            <Button
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => setSchemaMigrationDialogOpen(true)}
            >
              Migration Rules
            </Button>
            <Tooltip title="Apply migration rules to transform role schemas in all validated drafts. Use this to fix schema mismatches before publishing.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleTransformSchemas}
                  disabled={transformingSchemas || validationReports.length === 0}
                >
                  {transformingSchemas ? 'Transforming...' : 'Transform Schemas'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Button onClick={() => setValidationDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schema Migration Rules Dialog */}
      <Dialog
        open={schemaMigrationDialogOpen}
        onClose={() => setSchemaMigrationDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Schema Migration Rules
          <IconButton onClick={() => setSchemaMigrationDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SchemaMigrationManager />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Draft</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to delete draft <strong>"{draftToDelete?.name}"</strong>?
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            This action cannot be undone. All versions and data associated with this draft will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDraft}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <LinearProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={versionDialogOpen} onClose={() => setVersionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Version History: {selectedDraftForVersions?.name}
        </DialogTitle>
        <DialogContent>
          {loadingVersions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          ) : versions.length === 0 ? (
            <Alert severity="info">No versions found for this draft.</Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Versions: {versions.length}
              </Typography>
              <List>
                {versions.map((version, index) => (
                  <Accordion key={index} defaultExpanded={index === 0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography variant="subtitle2">
                          Version {version.version || index}
                        </Typography>
                        <Chip
                          label={version.publish ? 'Published' : 'Draft'}
                          color={version.publish ? 'success' : 'default'}
                          size="small"
                        />
                        {index === 0 && (
                          <Chip label="Latest" color="primary" size="small" />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Version"
                            secondary={version.version || 'N/A'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Note"
                            secondary={version.note || 'No note'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="References"
                            secondary={`${version.references?.length || 0} reference(s)`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Payloads"
                            secondary={`${version.payloads?.length || 0} payload(s)`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Published"
                            secondary={version.publish ? 'Yes' : 'No'}
                          />
                        </ListItem>
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Match Selection Dialog */}
      <Dialog open={matchDialogOpen} onClose={() => setMatchDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Select Replacement for {currentMatch?.detail.type}
        </DialogTitle>
        <DialogContent>
          {currentMatch && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Original UUID:</strong> {currentMatch.detail.uuid}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {currentMatch.detail.message}
                </Typography>
              </Alert>

              <Typography variant="subtitle2" gutterBottom>
                Suggested Matches:
              </Typography>

              {currentMatch.detail.suggestedMatches && currentMatch.detail.suggestedMatches.length > 0 ? (
                <RadioGroup value={selectedMatchUuid} onChange={(e) => { setSelectedMatchUuid(e.target.value); setManualUuidInput(''); }}>
                  {currentMatch.detail.suggestedMatches.map((match: SuggestedMatch) => (
                    <Paper key={match.uuid} variant="outlined" sx={{ p: 2, mb: 1 }}>
                      <FormControlLabel
                        value={match.uuid}
                        control={<Radio />}
                        label={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1">
                                {match.name}
                              </Typography>
                              <Chip
                                label={match.confidence}
                                color={getConfidenceColor(match.confidence) as any}
                                size="small"
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              UUID: {match.uuid}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {match.matchReason}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  ))}
                </RadioGroup>
              ) : (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No suggested matches found. Browse available resources or enter a UUID manually.
                  </Alert>

                  {/* Available Resources Browser */}
                  {loadingResources ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Loading available {currentMatch.detail.type} resources...</Typography>
                    </Box>
                  ) : availableResources.length > 0 ? (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Available {currentMatch.detail.type} resources in this environment:
                      </Typography>
                      <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                        <RadioGroup value={selectedMatchUuid} onChange={(e) => { setSelectedMatchUuid(e.target.value); setManualUuidInput(''); }}>
                          {availableResources.map((resource) => (
                            <Paper key={resource.uuid} variant="outlined" sx={{ p: 1.5, m: 1 }}>
                              <FormControlLabel
                                value={resource.uuid}
                                control={<Radio size="small" />}
                                label={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {resource.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                      {resource.uuid}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Paper>
                          ))}
                        </RadioGroup>
                      </Paper>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => loadAvailableResources(currentMatch.detail.type)}
                        sx={{ mb: 1 }}
                      >
                        Browse Available Resources
                      </Button>
                    </Box>
                  )}

                  {/* Manual UUID Input */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Or enter UUID manually:
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter resource UUID (e.g., 16ff2d4d-c531-480e-b0d3-ae4d75c56e91)"
                      value={manualUuidInput}
                      onChange={(e) => { setManualUuidInput(e.target.value); setSelectedMatchUuid(''); }}
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmMatch}
            variant="contained"
            disabled={!selectedMatchUuid && !manualUuidInput.trim()}
          >
            Confirm Mapping
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={confirmPublishOpen} onClose={() => setConfirmPublishOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Publish Draft as Scenario</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to publish draft <strong>"{publishTarget?.draftName}"</strong> as a scenario?
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            This will create a new scenario that can be deployed on RangeOS instances.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPublishOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePublishDraft}
            variant="contained"
            color="primary"
            startIcon={<PublishIcon />}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Draft</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={importTabValue} onChange={(e, newValue) => setImportTabValue(newValue)}>
              <Tab label="Local File" />
              <Tab label="S3" />
              <Tab label="RangeOS" />
            </Tabs>
          </Box>

          {/* Local File Tab */}
          {importTabValue === 0 && (
            <Box sx={{ mt: 2 }}>
              <input
                accept=".json"
                style={{ display: 'none' }}
                id="import-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="import-file-input">
                <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                  {importFile ? importFile.name : 'Choose File'}
                </Button>
              </label>

              <TextField
                fullWidth
                label="Draft Name"
                value={importDraftName}
                onChange={(e) => setImportDraftName(e.target.value)}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="Description (Optional)"
                value={importDraftDescription}
                onChange={(e) => setImportDraftDescription(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
            </Box>
          )}

          {/* S3 Tab */}
          {importTabValue === 1 && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>S3 Environment</InputLabel>
                <Select
                  value={importS3Environment}
                  onChange={(e) => handleImportS3EnvironmentChange(e.target.value)}
                  label="S3 Environment"
                >
                  {s3Environments.map((env) => (
                    <MenuItem key={env.environmentId} value={env.prefix}>
                      {env.environmentName} ({env.prefix})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loadingImportS3Files && <LinearProgress sx={{ mb: 2 }} />}

              {importS3Files.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select File</InputLabel>
                  <Select
                    value={selectedImportS3File}
                    onChange={(e) => setSelectedImportS3File(e.target.value)}
                    label="Select File"
                  >
                    {importS3Files.map((file) => {
                      const sizeKB = (file.size / 1024).toFixed(1);
                      const date = new Date(file.lastModified).toLocaleDateString();
                      return (
                        <MenuItem key={file.key} value={file.key}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2">{file.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {sizeKB} KB - {date}
                            </Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                label="Draft Name"
                value={importDraftName}
                onChange={(e) => setImportDraftName(e.target.value)}
                sx={{ mb: 2 }}
                required
              />
            </Box>
          )}

          {/* RangeOS Tab */}
          {importTabValue === 2 && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Source RangeOS Environment</InputLabel>
                <Select
                  value={importSourceRangeOS}
                  onChange={(e) => handleImportSourceRangeOSChange(e.target.value)}
                  label="Source RangeOS Environment"
                >
                  {environments.map((env) => (
                    <MenuItem key={env.id} value={env.id}>
                      {env.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loadingImportDrafts && <LinearProgress sx={{ mb: 2 }} />}

              {importSourceDrafts.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Draft</InputLabel>
                  <Select
                    value={selectedImportDraft}
                    onChange={(e) => setSelectedImportDraft(e.target.value)}
                    label="Select Draft"
                  >
                    {importSourceDrafts.map((draft) => (
                      <MenuItem key={draft.id} value={draft.id}>
                        {draft.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                label="Draft Name (Optional - uses source name if empty)"
                value={importDraftName}
                onChange={(e) => setImportDraftName(e.target.value)}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>
            Cancel
          </Button>
          <Button
            onClick={
              importTabValue === 0
                ? handleImportFromLocal
                : importTabValue === 1
                ? handleImportFromS3
                : handleImportFromRangeOS
            }
            variant="contained"
            color="primary"
            disabled={
              importing ||
              (importTabValue === 0 && (!importFile || !importDraftName)) ||
              (importTabValue === 1 && (!selectedImportS3File || !importDraftName)) ||
              (importTabValue === 2 && !selectedImportDraft)
            }
            startIcon={<FileUploadIcon />}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export to S3 Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Draft to S3</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Export draft <strong>"{exportDraft?.name}"</strong> to S3 storage
          </Alert>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Target S3 Environment</InputLabel>
            <Select
              value={exportS3Environment}
              onChange={(e) => setExportS3Environment(e.target.value)}
              label="Target S3 Environment"
            >
              {s3Environments.map((env) => (
                <MenuItem key={env.environmentId} value={env.prefix}>
                  {env.environmentName} ({env.prefix})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            The draft will be exported to the rangeos/drafts folder in S3.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            onClick={confirmExportToS3}
            variant="contained"
            color="primary"
            disabled={exporting || !exportS3Environment}
            startIcon={<CloudUploadIcon />}
          >
            {exporting ? 'Exporting...' : 'Export to S3'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={closeExportMenu}
      >
        <MenuItem onClick={handleExportToLocal}>
          <ListItemIcon>
            <GetAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export to Local File" />
        </MenuItem>
        <MenuItem onClick={handleExportToS3}>
          <ListItemIcon>
            <CloudUploadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export to S3" />
        </MenuItem>
      </Menu>
    </Box>
  );
};
